/**
 * The web build's `platformProviders` from its VITE_* values (launch plan 2A), the counterpart of
 * `native/config.ts` for a browser: H5 Games Ads, Stripe checkout, Web Push and the sign-in
 * configs of `webSignIn`, plus the Sentry DSN for crash reports.
 *
 * Every value is public — VITE_* ends up in the bundle — so these are ids, never secrets, and
 * `.env.web` carries the committed defaults.
 *
 * An unset family degrades to off rather than throwing: `createProviders` refuses to boot when
 * `ads=h5` has no publisher id or `iap=stripe` has no return URL, and a web build is served the
 * moment it is built, with nobody watching a console. `providersFor` therefore reports what is
 * actually configured, and `main.ts` asks for `auto` only where the config is there.
 *
 *   VITE_H5_ADS_CLIENT        ca-pub-… (AdSense publisher; with it the game may offer rewarded ads)
 *   VITE_H5_ADS_TEST          '1' adds data-adbreak-test="on"
 *   VITE_STRIPE_RETURN_URL    where Stripe sends the payer back; default the page origin + '/'
 *   VITE_VAPID_PUBLIC_KEY     the API's web-push key (GET /v1/push/vapid also serves it)
 *   VITE_SENTRY_DSN           unset: crash reports go to the console and /v1/client-errors only
 */
import type { ProvidersConfig } from '@mp/adapters/providers';
import { webSignIn } from './webSignIn.js';

type Env = Readonly<Record<string, unknown>>;

const value = (env: Env, key: string): string | undefined => {
  const v = env[key];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined;
};

/** The provider families a web build can actually run, given what the bundle was built with. */
export interface WebProviders {
  config: Pick<ProvidersConfig, 'auth' | 'ads' | 'iap' | 'push' | 'crash'>;
  /** `ads=…,iap=…,push=…,auth=auto`: each family is `auto` only where its config is present. */
  selection: string;
}

export function webProviders(env: Env, origin: string, release?: string): WebProviders {
  const h5 = value(env, 'VITE_H5_ADS_CLIENT');
  const stripeReturn = value(env, 'VITE_STRIPE_RETURN_URL') ?? `${origin}/`;
  const vapid = value(env, 'VITE_VAPID_PUBLIC_KEY');
  const sentry = value(env, 'VITE_SENTRY_DSN');
  // Stripe needs no publishable key in the browser: checkout is a redirect to a session the API
  // creates. The return URL is the whole of its client config, so it is always available.
  const config: WebProviders['config'] = {
    auth: webSignIn(env, origin),
    ...(h5 ? { ads: { h5: { client: h5, ...(value(env, 'VITE_H5_ADS_TEST') === '1' ? { testMode: true } : {}) } } } : {}),
    iap: { stripe: { returnUrl: stripeReturn } },
    ...(vapid ? { push: { vapidPublicKey: vapid, swUrl: '/sw.js' } } : {}),
    ...(sentry || release ? { crash: { ...(sentry ? { sentryDsn: sentry } : {}), ...(release ? { release } : {}) } } : {}),
  };
  const selection = [`ads=${h5 ? 'auto' : 'none'}`, 'iap=auto', `push=${vapid ? 'auto' : 'none'}`, 'auth=auto'].join(',');
  return { config, selection };
}
